import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Box, Typography, useTheme } from '@mui/material';

import {
  AiInstructionsTranscriptComponent,
  ContentSnippetTranscriptComponent,
  TranscriptComponent,
} from '../ConversationTranscript/types';
import { AddKnowledgeMenuButton } from './AddKnowledgeMenuButton';
import { useGetInstructionsQuery } from 'src/services/ai-instruction/aiInstructionApi';
import { useListContentSnippetsQuery } from 'src/services/content-snippets/contentSnippetsApi';
import { Routes } from 'src/utils/enums';

export const AiInstructionsSection = ({
  onCreateNewInstruction,
  transcriptComponents,
}: {
  onCreateNewInstruction: (newId: string) => void;
  transcriptComponents: TranscriptComponent[];
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  const aiInstructionIds = useMemo(() => {
    return Array.from(
      new Set(
        transcriptComponents
          .filter(c => c.message_type === 'ai_instructions')
          .flatMap(
            c => (c as AiInstructionsTranscriptComponent).ai_instruction_ids,
          ),
      ),
    );
  }, [transcriptComponents]);

  const contentSnippetIds = useMemo(() => {
    return Array.from(
      new Set(
        transcriptComponents
          .filter(c => c.message_type === 'content_snippet')
          .flatMap(
            c => (c as ContentSnippetTranscriptComponent).content_snippet_ids,
          ),
      ),
    );
  }, [transcriptComponents]);

  const { data: allInstructions } = useGetInstructionsQuery({
    draftMode: 'any',
  });

  const { data } = useListContentSnippetsQuery({
    draftMode: 'any',
  });
  const allContentSnippets = useMemo(() => {
    return data?.snippets || [];
  }, [data?.snippets]);

  const usedInstructions = useMemo(() => {
    if (!allInstructions || aiInstructionIds.length === 0) {
      return [];
    }

    return aiInstructionIds
      .map(id =>
        allInstructions.find(
          instruction => instruction.ai_instruction_id === id,
        ),
      )
      .filter(Boolean)
      .map(instruction => ({
        id: instruction?.ai_instruction_id || '',
        name: instruction?.name || '',
      }));
  }, [allInstructions, aiInstructionIds]);

  const usedContentSnippets = useMemo(() => {
    if (!allContentSnippets || contentSnippetIds.length === 0) {
      return [];
    }

    return contentSnippetIds
      .map(id =>
        allContentSnippets.find(snippet => snippet.content_snippet_id === id),
      )
      .filter(Boolean)
      .map(snippet => ({
        id: snippet?.content_snippet_id || '',
        name: snippet?.name || '',
      }));
  }, [allContentSnippets, contentSnippetIds]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      gap='8px'
      padding={'16px 16px 0'}
    >
      {transcriptComponents.some(
        component => component.message_type === 'ai_instructions',
      ) && (
        <Box
          display='flex'
          flexDirection='column'
          gap='8px'
          paddingBottom={'16px'}
        >
          <Box
            alignItems='center'
            display='flex'
            justifyContent='space-between'
          >
            <Typography color={palette.colors.grey[600]} variant='font12'>
              {t('aiInstructionsSection.knowledgeCoaching')}
            </Typography>
            <AddKnowledgeMenuButton onSuccess={onCreateNewInstruction} />
          </Box>
          <Box display='flex' flexDirection='column' gap='2px'>
            {usedInstructions.map(instruction => (
              <Typography
                color={palette.colors.blue[600]}
                component={Link}
                key={instruction.id}
                rel='noreferrer'
                target='_blank'
                to={Routes.KNOWLEDGE + `?aiInstruction=${instruction.id}`}
                variant='font12'
              >
                {instruction.name}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      {transcriptComponents.some(
        component => component.message_type === 'content_snippet',
      ) && (
        <Box
          display='flex'
          flexDirection='column'
          gap='8px'
          paddingBottom={'16px'}
        >
          <Box
            alignItems='center'
            display='flex'
            justifyContent='space-between'
          >
            <Typography color={palette.colors.grey[600]} variant='font12'>
              {t('aiInstructionsSection.contentSnippets')}
            </Typography>
          </Box>
          <Box display='flex' flexDirection='column' gap='2px'>
            {usedContentSnippets.map(snippet => (
              <Typography
                color={palette.colors.blue[600]}
                component={Link}
                key={snippet.id}
                rel='noreferrer'
                target='_blank'
                to={`${Routes.KNOWLEDGE}?tab=content+snippets&contentSnippet=${snippet.id}`}
                variant='font12'
              >
                {snippet.name}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
